import FastAPIClient from "./services/apiClient";
import config from "./config";
import { v4 as uuidv4 } from "uuid";
export const client = new FastAPIClient()

// Local mapping for currency codes to symbols
export const currencyCodeToSymbolMap = {
    "NGN": "₦",
    "USD": "$",
    "EUR": "€",
    "GBP": "£",
    "JPY": "¥",
    "CAD": "C$",
    "AUD": "A$",
    "CHF": "CHF",
    "CNY": "¥",
    "INR": "₹",
    "BRL": "R$",
    "RUB": "₽",
    // Add more as needed
};

/**
 * Formats numbers with thousands separators and fixed decimals.
 * @param {number|string} amount - The value to format
 * @param {number} decimals - Number of decimal places (default 2)
 * @param {string} currency - Optional currency symbol (e.g., '₦', '$')
 * @returns {string} Formatted string (e.g., "₦ 1,250,500.00")
 */
export const formatCurrency = (amount, decimals = 2, currency = '', csmap = null) => {
    if (currency && csmap) {
        currency = csmap[currency];
    }
    if (amount === null || amount === undefined || isNaN(amount)) {
        return `${currency ? currency : ''}0.${'0'.repeat(decimals)}`;
    }

    const numericValue = typeof amount === 'string' ? parseFloat(amount) : amount;

    // Uses the browser's native Intl.NumberFormat for high performance and local accuracy
    const formattedNumber = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(numericValue);

    return currency ? `${currency}${formattedNumber}` : formattedNumber;
};

export const generateUniquePoNumber = () => {
    // 1. Use uuidv4() instead of crypto.randomUUID()
    // 2. uuidv4() returns a standard UUID string like:
    //    'a1b2c3d4-e5f6-7890-1234-567890abcdef'

    // The rest of your logic remains the same to format it
    // into a 15-character, hyphen-free, uppercase string.
    return uuidv4()
        .replace(/-/g, '') // Removes all hyphens: 'a1b2c3d4e5f678901234567890abcdef'
        .toUpperCase()      // Converts to uppercase
        .substring(0, 15);  // Takes the first 15 characters
};

// export const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);
export const capitalize = str => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

export const API_BASE_PATH = `${config.apiBasePath}/api/v1`
export const API_ROOT_PATH = `${config.apiBasePath}`

export const fetchUsages = async () => {
    const data = await client.fetchData('/attachment/usages')
    return data.attachmentusages
}

export const fetchTotalReceivableBalance = async (payload) => {
    const data = await client.postData('/accounting/customer-total-receivable-balance', payload)
    return data
}

export const fetchTotalNetBalance = async(payload) => {
    const data = await client.postData('/accounting/customer-total-net-balance', payload)
    return data
}

export const fetchCatenttypes = async () => {
    const data = await client.fetchData('/product/catenttypes')
    return data.catenttypes
}

export const fetchRoles = async () => {
    const data = await client.fetchData('/auth/roles')
    return data
}

export const fetchOrganizations = async () => {
    const data = await client.fetchData('/users/organizations')
    return data.organizations
}

export const fetchQtyunits = async () => {
    const data = await client.fetchData('/uom/qtyunits')
    return data.qtyunits
}

export const fetchAllOrders = async () => {
    const data = await client.fetchData('/sales/orders')
    return data
}

export const fetchEndOfDay = async (payload) => {
    const data = await client.postData('/sales/end-of-day', payload)
    return data
}

export const fetchOrdersByPeriod = async (payload) => {
    const data = await client.postData('/sales/orders-by-period', payload)
    return data
}

export const fetchRetailDashboard = async (payload) => {
    const data = await client.postData('/dashboard/retail-dashboard-summaries', payload)
    return data
}

export const fetchRetailSalesSeries = async (payload) => {
    const data = await client.postData('/dashboard/retail-sales-series', payload)
    return data
}

export const fetchOrdersWindow = async (payload) => {
    const data = await client.postData('/sales/orders-window', payload)
    return data
}

export const fetchAllBankAccounts = async () => {
    const data = await client.fetchData('/accounting/util-bank-accounts')
    return data
}

export const fetchCustomerReturns = async (payload) => {
    const data = await client.postData('/store/customer-returns', payload)
    return data
}

export const fetchOrderByStoreAndIdempotency = async (payload) => {
    const data = await client.postData('/sales/order-by-store-idempotency', payload)
    return data
}

export const fetchInvoicesForStaff = async (payload) => {
    const data = await client.postData('/sales/invoices', payload)
    return data.orders
}

export const fetchReceiptsForStaff = async (payload) => {
    const data = await client.postData('/sales/receipts', payload)
    return data.orders
}

export const fetchInvoiceForStaff = async (payload) => {
    const data = await client.postData('/sales/invoice', payload)
    return data.order
}

export const fetchReceiptForStaff = async (payload) => {
    const data = await client.postData('/sales/receipt', payload)
    return data.order
}

export const fetchSettings = async () => {
    const data = await client.fetchData('/store/get-settings')
    return data
}

export const salesSourceDocument = async (payload) => {
    const data = await client.postData('/sales/source-document', payload)
    return data.order
}

export const fetchPaymentProcessors = async () => {
    const data = await client.fetchData('/sales/get-payment-processor')
    return data
}

export const fetchLedgerSummaryList = async () => {
    const data = await client.fetchData('/accounting/ledger-summary')
    return data
}

export const goodsReceiptDocument = async (payload) => {
    const data = await client.postData('/inventory/goods-receipt-document', payload)
    return data
}

export const fetchPaymentsReceived = async () => {
    const data = await client.fetchData('/sales/payments-received')
    return data
}

export const getPendingOrders = async (payload) => {
    const data = await client.postData('/sales/pending-orders', payload)
    return data
}

export const fetchStoresForIndexPage = async (payload) => {
    const data = await client.postData('/store/index-stores', payload)
    return data
}

export const fetchCustomerStoreLOC = async (payload) => {
    const data = await client.postData('/sales/customer-store-line-of-credit', payload)
    return data
}

export const fetchStockHistoryReport = async (payload) => {
    const data = await client.postData('/inventory/stock-history-report', payload)
    return data
}

export const fetchInventoryClosingReport = async (payload) => {
    const data = await client.postData('/inventory/closing-inventory-report', payload)
    return data
}

export const fetchTransactionsForDate = async (payload) => {
    const data = await client.postData('/accounting/transactions-by-date', payload);
    return data
}

export const fetchVendorSubLedgerReport = async (payload) => {
    const data = await client.postData('/accounting/vendor-account-subledger-report', payload)
    return data
}

export const fetchVendorSubledgerReportCSV = async (payload) => {
    const data = await client.postData('/export/vendor-subledger-export-csv', payload)
    return data
}

export const fetchCustomerListExportCSV = async (payload) => {
    const data = await client.postData("/export/customer-list-export-csv", payload)
    return data
}

export const fetchAccountLedgerReportCSV = async (payload) => {
    const data = await client.postData("/export/account-ledger-export-csv", payload)
    return data
}

export const fetchGroupsForInvoice = async (payload) => {
    const data = await client.postData('/users/groups-for-invoice', payload);
    return data
}

export const fetchGroupsUtils = async () => {
    const data = await client.fetchData("/users/fetch-groups-utils")
    return data
}

export const fetchGroup = async (group_id) => {
    const data = await client.fetchData(`/users/group/${group_id}`)
    return data
}

export const fetchGroups = async (payload) => {
    const data = await client.postData('/users/groups', payload)
    return data
}

export const fetchBranches = async (payload) => {
    const data = await client.fetchData('/store/branches')
    return data
}

export const fetchBranch = async (store_id) => {
    const data = await client.fetchData(`/store/branch/${store_id}`)
    return data
}

export const fetchStores = async (payload) => {
    const data = await client.postData('/store/stores', payload)
    return data.stores
}

export const fetchStoresWhereStaffAssigned = async (payload) => {
    const data = await client.postData('/store/stores-where-staff-assigned', payload)
    return data.stores
}

export const fetchSalesPerformanceAnalysis = async (payload) => {
    const data = await client.postData("/sales/sales-performance-analysis", payload)
    return data
}

export const fetchCustomerLedgerExportCSV = async (payload) => {
    const data = await client.postData("/export/customer-ledger-report-csv", payload)
    return data
}

export const fetchSalesPerformanceExportCSV = async (payload) => {
    const data = await client.postData("/export/sales-performance-export-csv", payload)
    return data
}

export const fetchClosingInventoryExportCSV = async (payload) => {
    const data = await client.postData("/export/closing-inventory-export-csv", payload)
    return data
}

export const fetchStaffViewCreditline = async (payload) => {
    const data = await client.postData('/sales/staff-view-lines-of-credit-for-customer', payload)
    return data
}

export const fetchBatchTaxToCategoryItemUtil = async (payload) => {
    const data = await client.postData('/product/batch-catentries-tax-util', payload)
    return data
}

export const readTaxes = async (payload) => {
    const data = await client.postData('/sales/read-taxes', payload)
    return data
}

export const fetchCatentryDetails = async (payload) => {
    const data = await client.postData('/product/catentry-details', payload)
    return data
}

export const fetchCatentriesUtil = async (payload) => {
    const data = await client.postData('/product/catentry-util', payload)
    return data
}

export const fetchItemDetails = async (payload) => {
    const data = await client.postData('/product/item-details', payload)
    return data
}

export const fetchCreditSales = async (payload) => {
    const data = await client.postData('/sales/credit-sales', payload)
    return data
}

export const checkpointOrganizations = async (username) => {
    const data = await client.fetchData(`/auth/search-orgs?email=${encodeURIComponent(username)}`)
    return data
}

export const fetchStoreWithStaff = async (store_id) => {
    const data = await client.fetchData(`/store/store-with-staff/${store_id}`)
    return data
}

export const fuzzySearchAccounts = async (tag) => {
    const data = await client.fetchData(`/accounting/fuzzy-search-account-name/${tag}`)
    return data
}

export const fetchInventoryValuationForStore = async (store_id) => {
    const data = await client.fetchData(`/inventory/inventory-valuation-report/${store_id}`)
    return data
}

export const fetchDetailTypeForAccountType = async (accounttype_id) => {
    const data = await client.fetchData(`/accounting/detail-type-for-account-type/${accounttype_id}`)
    return data
}

export const fetchGoodsReceiptHistory = async (payload) => {
    const data = await client.postData('/inventory/goods-receipt-history', payload)
    return data
}

export const fetchGoodsReceiptHistoryCSV = async (payload) => {
    const data = await client.postData('/export/goods-receipt-history-export-csv', payload)
    return data
}

export const fetchTransactionmap = async (payload) => {
    const data = await client.postData('/accounting/tm-posting-accounts', payload)
    return data
}

export const fetchExpenseTransactionsFromFundingAccount = async (payload) => {
    const data = await client.postData('/accounting/expense-account-transactions', payload)
    return data
}

export const fetchInventoryForStore = async (payload) => {
    const data = await client.postData('/inventory/inventory', payload)
    return data
}

export const fetchInventoryForStoreSales = async (payload) => {
    const data = await client.postData('/inventory/inventory-for-sales',payload)
    return data
}

export const fetchServicesForStoreSale = async (payload) => {
    const data = await client.postData('/inventory/services-for-sales',payload)
    return data
}

export const lotsForCatentryByStoreStaff = async (payload) => {
    const data = await client.postData('/inventory/lots-for-catentry', payload)
    return data
}

export const fetchCustomerList = async (payload) => {
    const data = await client.postData('/users/customer-list', payload)
    return data
}

export const fetchCustomerListV2 = async (store_id) => {
    const data = await client.fetchData(`/users/customer-list-v2/${store_id}`)
    return data
}

export const fetchCountries = async () => {
    const data = await client.fetchData('/geo/countries')
    return data
}

export const fetchUnreadNotifications = async () => {
    const data = await client.fetchData('/notification/unread')
    return data
}

export const fetchStatesForCountry = async (country_abbr) => {
    const data = await client.fetchData(`/geo/states/${country_abbr}`)
    return data
}

export const fetchCatentriesForStore = async (payload) => {
    const data = await client.postData('/inventory/catentries-for-store', payload)
    return data
}

export const fetchUtilCatentriesForStore = async (payload) => {
    const data = await client.postData('/inventory/util-catentries-for-store', payload)
    return data
}

export const fetchProductsForStoreSimple = async (payload) => {
    const data = await client.postData('/product/catentries-for-store-simple', payload)
    return data
}

export const fetchProductsForStore = async (payload) => {
    const data = await client.postData('/product/catentries-for-store', payload)
    return data
}

export const fetchProductsForOrg = async () => {
    const data = await client.fetchData('/product/catentries-for-org')
    return data
}

export const fetchNamedUsers = async (payload) => {
    const data = await client.postData('/users/named-members', payload)
    return data
}

export const fetchAuditLogs = async () => {
    const data = await client.fetchData('/auditlog/audit-trail')
    return data
}

export const fetchCurrentEndpointPermissions = async () => {
    const data = await client.fetchData('/auth/permissions')
    return data
}

export const fetchCurrencies = async () => {
    const data = await client.fetchData('/currency/currencies')
    return data.currencies
}

export const fetchTransactionTypes = async () => {
    const data = await client.fetchData('/accounting/transaction-types')
    return data
}

export const fetchStoresWithStaff = async () => {
    const data = await client.fetchData('/store/stores-with-staff')
    return data
}

export const fetchAllAccountsWithDetails = async () => {
    const data = await client.fetchData('/accounting/accounts-with-details')
    return data
}

// export const fetchAccountDetails = async(accountId) => {
//     const data = await client.fetchData(`/accounting/account-transactions/${accountId}`)
//     return data
// }

export const fetchAccountDetails = async (payload) => {
    const data = await client.postData(`/accounting/account-transactions`, payload)
    return data
}

export const fetchOrderDetails = async (orders_id) => {
    const data = await client.fetchData(`/sales/order-details/${orders_id}`)
    return data
}

export const fetchVendorCredit = async (vendor_id) => {
    const data = await client.fetchData(`/vendor/fetch-vendor-credit/${vendor_id}`)
    return data
}

export const fetchCustomerDepositBalance = async (customer_id) => {
    const data = await client.fetchData(`/sales/fetch-customer-deposit/${customer_id}`)
    return data
}

export const fetchPurchaseOrdersForVendor = async (vendorId) => {
    const data = await client.fetchData(`/vendor/purchase-orders/${vendorId}`)
    return data
}

export const fetchPOForReturn = async (payload) => {
    const data = await client.postData('/vendor/po-for-return-to-vendor', payload)
    return data
}

export const fetchUtilPurchaseOrders = async (vendorId) => {
    const data = await client.fetchData(`/vendor/util-purchase-orders/${vendorId}`)
    return data
}

export const fetchPNLReport = async (payload) => {
    const data = await client.postData('/accounting/pnl-report', payload)
    return data
}

export const fetchGeneralJournalReport = async (payload) => {
    const data = await client.postData('/accounting/general-journal-report', payload)
    return data
}

export const fetchTrialBalance = async (payload) => {
    const data = await client.postData('/accounting/trial-balance', payload)
    return data
}

export const fetchGeneralLedgerReport = async (payload) => {
    const data = await client.postData('/accounting/general-ledger-report', payload)
    return data
}

export const fetchAccountLedgerReport = async (payload) => {
    const data = await client.postData('/accounting/account-ledger-report', payload)
    return data
}

export const fetchCustomerLedgerReport = async (payload) => {
    const data = await client.postData('/accounting/customer-ledger', payload)
    return data
}

export const fetchVendors = async (payload) => {
    const data = await client.postData('/vendor/vendors', payload)
    return data
}

export const fetchCatentryTransactionMaps = async (payload) => {
    const data = await client.postData('/accounting/catentry-transaction-maps', payload)
    return data
}

export const fetchCatentriesForCatalog = async (payload) => {
    const data = await client.postData('/product/catentries-for-catalog', payload)
    return data
}

export const fetchCatalogsWithCategories = async (payload) => {
    const data = await client.postData('/product/catalog-with-categories', payload)
    return data.catalogs
}

export const fetchCatalogs = async (payload) => {
    const data = await client.postData('/product/catalogs', payload)
    return data.catalogs
}

export const fetchCategories = async (payload) => {
    const data = await client.postData('/product/categories', payload)
    return data.categories
}

export const fetchAttributes = async (payload) => {
    const data = await client.postData('/attr/attributes', payload)
    return data.attrs
}

export const fetchCatentries = async (payload) => {
    const data = await client.postData('/product/catentries', payload)
    return data
}

export const fetchStaffAppropriateCatentries = async (payload) => {
    const data = await client.postData('/product/catentries-for-staff', payload)
    return data
}

export const fetchStoretaxes = async (payload) => {
    const data = await client.postData('/store/store-taxes', payload)
    return data
}

export const fetchAccounttypes = async () => {
    const data = await client.fetchData('/accounting/account-types')
    return data.accounttypes
}

export const fetchAccounts = async (payload) => {
    const data = await client.postData('/accounting/accounts', payload)
    return data
}

export const fetchUserProfile = async (payload) => {
    const data = await client.postData('/user/profile', payload)
    return data
}

export const fetchAllProfiles = async (payload) => {
    const data = await client.postData('/user/profiles', payload)
    return data
}

export const fetchUtilUsers = async (payload) => {
    const data = await client.postData('/user/util-users', payload)
    return data
}

export const fetchUtilAccounts = async (payload) => {
    const data = await client.postData('/accounting/util-accounts', payload)
    return data
}

export const fetchTransactionAccountMaps = async (payload) => {
    const data = await client.postData('/accounting/account-mappings', payload)
    return data
}

export const fetchDiscountRules = async (payload) => {
    const data = await client.postData('/accounting/discounts', payload)
    return data
}

export const fetchClassrooms = async (payload) => {
    const data = await client.postData('/school/classrooms', payload)
    return data
}

export const fetchUtilCustomers = async (payload) => {
    const data = await client.postData('/users/util-customers', payload)
    return data
}

export const fetchPOSCustomers = async (payload) => {
    const data = await client.postData('/users/point-of-sale-customers', payload)
    return data
}

export const fetchUtilStaff = async (payload) => {
    const data = await client.postData('/users/util-staff', payload)
    return data
}

export const fetchSubjects = async (payload) => {
    const data = await client.postData('/school/subjects', payload)
    return data
}

export const fetchTeacherNotes = async (payload) => {
    const data = await client.postData('/school/teacher-notes', payload)
    return data
}

export const fetchTeachers = async (payload) => {
    const data = await client.postData('/school/teachers', payload)
    return data
}

export const fetchStudents = async (payload) => {
    const data = await client.postData('/school/students', payload)
    return data
}


export const fetchCashToCash = async (payload) => {
    const data = await client.postData('/dashboard/cash-to-cash', payload);
    return data;
}

export const fetchVendorPriceLagAnalysis = async (payload) => {
    const data = await client.postData('/dashboard/vendor-price-lag', payload);
    return data;
}

export const fetchCustomerRiskFrequency = async (payload) => {
    const data = await client.postData('/dashboard/customer-risk-frequency', payload);
    return data;
}

export const fetchGoodsReceiptBankAlignment = async (payload) => {
    const data = await client.postData('/dashboard/receipt-bank-alignment', payload);
    return data;
}

export const fetchZeroElasticityAnalysis = async (payload) => {
    const data = await client.postData('/dashboard/zero-elasticity', payload)
    return data
}

export const fetchVendorPivotPointAnalysis = async (payload) => {
    const data = await client.postData('/dashboard/vendor-pivot-point', payload)
    return data
}

export const fetchRevenuePerformanceDashboard = async (payload) => {
    const data = await client.postData('/dashboard/revenue', payload);
    return data
}

export const fetchCashflowLiquidityAnalysis = async (payload) => {
    const data = await client.postData('/dashboard/cashflow-liquidity', payload);
    return data;
}


export const initializeTransfer = async (data) => await client.postData('/paystack/initialize-transfer', data);
export const verifyTransfer = async (data) => await client.postData('/paystack/verify-transfer', data);
export const getCustomerVirtualAccounts = async (customerId) => await client.fetchData(`/paystack/customers/${customerId}/virtual_accounts`);
export const getCustomerTransactions = async (customerId) => await client.fetchData(`/paystack/customers/${customerId}/transactions`);
export const listSubaccounts = async () => await client.fetchData('/paystack/subaccounts');
export const createSubaccount = async (data) => await client.postData('/paystack/create-subaccount', data);
export const listBanks = async () => await client.fetchData('/paystack/banks');
export const getTransactionStatus = async (internalReference) => await client.fetchData(`/paystack/transactions/${internalReference}/status`);


export const notEmpty = (value) => (value !== null && value !== "" && value !== undefined)

export const decodeHTMLEntities = (encodedStr) => {
    const textArea = document.createElement('textarea')
    textArea.innerHTML = encodedStr
    return textArea.value
}

export const getFullName = (user) => {
    if (!user) return 'User'
    const parts = [
        user.first_name || '',
        user.middle_name || '',
        user.last_name || ''
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' ') : 'User';
}